import { z, ZodError } from 'zod';

/**
 * Validation Utilities
 * 
 * Epic 2 - Story 2.3: Runtime Type Safety
 * 
 * These utilities provide runtime validation using Zod schemas
 * with proper error handling and logging.
 */

/**
 * Validate data against a Zod schema
 * 
 * @param schema - The Zod schema to validate against
 * @param data - The data to validate
 * @param errorContext - Optional context string for better error messages
 * @returns The validated and typed data
 * @throws ValidationError if validation fails
 * 
 * @example
 * ```typescript
 * const user = validate(UserInfoSchema, apiResponse, 'findUserByEmail');
 * ```
 */
export function validate<T>(
  schema: z.ZodType<T>,
  data: unknown,
  errorContext?: string
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof ZodError || (error as any).name === 'ZodError') {
      // Handle both Zod v3 and v4 error structures
      const zodError = error as ZodError;
      const errors = (zodError as any).errors || (zodError as any).issues || [];
      
      console.error(
        `Validation error${errorContext ? ` in ${errorContext}` : ''}:`,
        {
          errors: errors,
          data: '[redacted]',
        }
      );

      throw new ValidationError(
        `Invalid data${errorContext ? ` in ${errorContext}` : ''}`,
        zodError
      );
    }
    throw error;
  }
}

/**
 * Safe validation that returns a result type instead of throwing
 * 
 * @param schema - The Zod schema to validate against
 * @param data - The data to validate
 * @returns Result object with success boolean and either data or error
 * 
 * @example
 * ```typescript
 * const result = validateSafe(UserInfoSchema, apiResponse);
 * if (result.success) {
 *   console.log(result.data.profile.email);
 * } else {
 *   console.error(result.error.errors);
 * }
 * ```
 */
export function validateSafe<T>(
  schema: z.ZodType<T>,
  data: unknown
): { success: true; data: T } | { success: false; error: ZodError } {
  const result = schema.safeParse(data);

  if (result.success) {
    return { success: true, data: result.data };
  } else {
    return { success: false, error: result.error };
  }
}

/**
 * Validation Error Class
 * 
 * Wraps ZodError with additional context and convenience methods
 */
export class ValidationError extends Error {
  public readonly zodError: ZodError;

  constructor(message: string, zodError: ZodError) {
    super(message);
    this.name = 'ValidationError';
    this.zodError = zodError;

    // Maintains proper stack trace for where error was thrown (only in V8)
    if ('captureStackTrace' in Error && typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, ValidationError);
    }
  }

  /**
   * Get all error messages as an array
   */
  getMessages(): string[] {
    // Zod v4 compatibility: check if errors exists, otherwise return issues
    const errors = (this.zodError as any).errors || (this.zodError as any).issues || [];
    return errors.map((err: any) => {
      const path = err.path && err.path.length > 0 ? err.path.join('.') : 'root';
      return `${path}: ${err.message}`;
    });
  }

  /**
   * Get the first error message
   */
  getFirstMessage(): string {
    const messages = this.getMessages();
    return messages[0] || 'Validation failed';
  }

  /**
   * Get formatted error string for display
   */
  toString(): string {
    return `${this.message}: ${this.getMessages().join('; ')}`;
  }
}

/**
 * Type guard to check if an error is a ValidationError
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Type guard to check if an error is a ZodError
 */
export function isZodError(error: unknown): error is ZodError {
  return error instanceof ZodError;
}

/**
 * Format a validation error for user display
 * 
 * @param error - The validation error
 * @returns User-friendly error message
 */
export function formatValidationError(error: ValidationError): string {
  const messages = error.getMessages();
  
  if (messages.length === 1) {
    return messages[0];
  }
  
  return `Multiple validation errors:\n${messages.map((m, i) => `${i + 1}. ${m}`).join('\n')}`;
}

/**
 * Validate API response and extract data
 * 
 * Helper for validating API responses that follow the common pattern:
 * { errorType: 0, data: {...} } or { errorType: non-zero, errorMessage: "..." }
 * 
 * @param schema - Schema for the data field
 * @param response - The API response
 * @param errorContext - Optional context for error messages
 * @returns The validated data
 * @throws ValidationError or Error
 */
export function validateApiResponse<T>(
  schema: z.ZodType<T>,
  response: unknown,
  errorContext?: string
): T {
  // First validate it's an object with errorType
  const baseResponseSchema = z.object({
    errorType: z.number(),
    errorMessage: z.string().optional(),
    data: z.unknown().optional(),
  });

  const baseResponse = validate(baseResponseSchema, response, errorContext);

  // Check if it's an error response
  if (baseResponse.errorType !== 0) {
    throw new Error(
      baseResponse.errorMessage || 'API returned error without message'
    );
  }

  // Validate the data field
  return validate(schema, baseResponse.data, errorContext);
}

